import {
	AnalyticsProjectionPoint,
	AnalyticsProjectionUnavailable,
	AnalyticsReportPreview,
	AnalyticsSeriesProjection,
	isAnalyticsProjection,
} from "../../../../../services/analytics.service";

/**
 * Chart model for a LINE preview, with the projected tail kept distinguishable
 * from the measurements (SIMS-7851).
 *
 * Extracted from the tab component rather than written inline for two reasons:
 * the merge rules below are the load-bearing part of "a reader must never
 * mistake a projection for a measurement", and jest excludes `index.tsx` from
 * coverage — logic left in the component would ship untested.
 */
export interface LineChartModel {
	/** One row per x tick. Projected rows carry `projected` and NO `value`. */
	data: Record<string, string | number>[];
	/** Keys for the solid, measured line(s). */
	seriesKeys: string[];
	/** The projected tail actually drawn (empty on a layered chart). */
	projectedPoints: AnalyticsProjectionPoint[];
	computed?: AnalyticsSeriesProjection;
	refusal?: AnalyticsProjectionUnavailable;
	isMulti: boolean;
}

export const buildLineChartModel = (
	preview: AnalyticsReportPreview,
): LineChartModel => {
	const series = preview.series ?? [];
	const isMulti = series.length > 0;

	// Recharts wants one object per x tick with a key per series. Order is taken
	// as given: for a LINE the point order IS the timeline, so it must not be
	// re-sorted or de-duplicated here.
	const labels = isMulti
		? series[0].points.map((p) => p.label)
		: (preview.metrics ?? []).map((m) => m.label);

	const data: Record<string, string | number>[] = labels.map((label, i) => {
		const point: Record<string, string | number> = { name: label };
		if (isMulti) {
			for (const s of series) {
				const v = s.points[i]?.value;
				point[s.name] = typeof v === "number" ? v : Number(v) || 0;
			}
		} else {
			const v = preview.metrics?.[i]?.value;
			point.value = typeof v === "number" ? v : Number(v) || 0;
		}
		return point;
	});

	const seriesKeys = isMulti ? series.map((s) => s.name) : ["value"];

	const projection = preview.projection;
	const computed =
		projection && isAnalyticsProjection(projection) ? projection : undefined;
	const refusal =
		projection && !isAnalyticsProjection(projection) ? projection : undefined;

	// A dashed continuation needs exactly one measured line to continue. On a
	// layered chart the projection is of the per-label TOTALS, which are not
	// drawn, so a dashed line would appear out of nowhere at the right edge. The
	// numbers are stated in the caption instead.
	const projectedPoints = !isMulti && computed ? computed.points : [];

	if (projectedPoints.length > 0 && data.length > 0) {
		// Seed the projected key on the LAST MEASURED point so the dashed line
		// starts from the final measurement instead of floating detached. That row
		// keeps its own `value`, so it is still drawn as a measurement too.
		data[data.length - 1].projected = data[data.length - 1].value;
		for (const p of projectedPoints) {
			// No `value` key: the solid line must terminate here, not run on.
			data.push({ name: p.label, projected: p.value });
		}
	}

	return { data, seriesKeys, projectedPoints, computed, refusal, isMulti };
};

/**
 * One sentence about a threshold crossing.
 *
 * Every branch is phrased as a rate carried forward, never as what is going to
 * happen. The backend refuses to produce a crossing it cannot justify, and the
 * wording here must not add confidence the number does not carry.
 */
export const describeCrossing = (
	projection: AnalyticsSeriesProjection,
): string => {
	const crossing = projection.crossing;
	if (!crossing) return "";
	const name = crossing.targetLabel
		? `${crossing.targetLabel} (${crossing.target})`
		: String(crossing.target);

	if (crossing.alreadyReached) {
		return `Already at or beyond ${name} — that is a measurement, not a projection.`;
	}
	if (crossing.neverReason === "FLAT") {
		return `Flat at the current rate, so ${name} is not reached.`;
	}
	if (crossing.neverReason === "MOVING_AWAY") {
		return `Moving away from ${name}, so the current rate does not reach it.`;
	}
	if (crossing.neverReason === "BEYOND_HORIZON") {
		return `${name} is not reached within any useful horizon at the current rate.`;
	}
	return `At the current rate, ${name} is reached during ${crossing.label} — about ${crossing.periodsAhead} ${projection.periodName}s after ${projection.lastActualLabel}.`;
};

/** The method statement shown under a projected chart. */
export const describeMethod = (
	projection: AnalyticsSeriesProjection,
): string => {
	const sign = projection.slopePerPeriod > 0 ? "+" : "";
	return `Projection: least-squares linear fit over ${projection.actualCount} ${projection.periodName}s ending ${projection.lastActualLabel} — ${sign}${projection.slopePerPeriod} per ${projection.periodName}, R² ${projection.rSquared.toFixed(2)}. It carries the observed rate forward unchanged; it is not a prediction, and projected points are excluded from any total.`;
};
